import { ChangeEvent } from 'react';
import { useFormContext } from 'react-hook-form';
import { SingleValue } from 'react-select';
import { AsyncCreatableSelect, TextInput } from '~/components';
import { BoardCertFormValues } from '../types';

interface Props {
  parentId: string | undefined;
  parentName: keyof BoardCertFormValues;
  selectName: keyof BoardCertFormValues;
  textInputName: keyof BoardCertFormValues;
  label: string;
  onChange: (option: SingleValue<{ value: string; label: string; id?: number }>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- react-select loadOptions return type
  loadOptions: (parentId: string) => Promise<any>;
  selectCyTag: string;
  inputCyTag: string;
  usePortal?: boolean;
  required?: boolean;
}
/**
 * Creates either an AsyncSelect of Input.
 * @param props
 * @returns
 */
export const AsyncSelectOrInput = (props: Props) => {
  const { watch, setValue } = useFormContext();
  const parentSelect = watch(props.parentName);
  // [MCV-1627] Loose `== null` on purpose: it matches both `undefined` (never
  // set) and `null` (cleared by BoardCertForm's cascade, which cannot use
  // `undefined` there without the parent being resurrected on remount). Do not
  // tighten this to `===` without giving that cascade another way to say
  // "absent".
  const disabled = parentSelect == null;
  if (props.parentId) {
    return (
      <AsyncCreatableSelect
        name={props.selectName}
        label={props.label}
        disabled={disabled}
        cyTag={props.selectCyTag}
        minChars={false}
        onChange={props.onChange}
        loadOptions={() => props.loadOptions(props.parentId as string)}
        usePortal={props.usePortal}
        required={props.required}
      />
    );
  } else {
    return (
      <TextInput
        name={props.textInputName}
        cyTag={props.inputCyTag}
        disabled={disabled}
        label={props.label}
        required={props.required}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const option = { label: e.target.value, value: e.target.value };
          setValue(props.selectName, option);
          // [MCV-1627] Run the same cascade the select branch runs. This branch
          // only mirrored the typed value into `selectName`, so editing a
          // CUSTOM parent -- the shape rendered whenever the parent has no id
          // -- left every descendant derived from the previous parent in place.
          // Visibly stale, and now that the schema and the payload both read
          // the free-text fallback, submitted as well.
          //
          // Safe from self-wipe by construction: each cascade handler in
          // BoardCertForm clears strictly its own descendants, never the field
          // it is attached to. And `setValue` updates react-hook-form state
          // without dispatching a DOM change event, so clearing a descendant
          // that happens to be another TextInput cannot re-enter this handler.
          props.onChange(option);
        }}
      />
    );
  }
};
